import {
  Autocomplete,
  Button,
  ButtonIcon,
  LoadingSpinner,
  Select,
  SelectOption,
} from '@data-products-and-ai/react-components';
import React, { useEffect, useState } from 'react';
import { BidContext } from '@/pages/Home/HomePage';
import useToken from '@/utils/useToken';
import { DataGrid } from '@mui/x-data-grid';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'; // Import Dialog components

interface Option {
  key: string;
  label: string;
}

const preSelectedColumns = [
  'Salesforce_Nr.',
  'Endcustomer_Name',
  'Breaker_Type',
  'Status',
  'Endprice',
  'Base_Price',
  'List_Price',
  'Techn-Equivalence',
  'Bursting_Plate',
  'Coil_Type',
  'Isolator_Mateial',
  'Secondary_Control',
  'Smart',
  'Support_Structure',
  'Transport_Packaging',
  'Point_ON_Wave_Switching',
];

const SimilarCases: React.FC = () => {
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [selectedEndCustomer, setselectedEndCustomer] = useState<string>('All');
  const [selectedFiscalYear, setselectedFiscalYear] = useState<string>('All');
  const [endCustomers, setEndCustomers] = useState<string[]>([]);
  const [fiscalYear, setFiscalYear] = useState<string[]>([]);
  const [historicalCases, setHistoricalCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [headers, setHeaders] = useState<
    { field: string; headerName: string }[]
  >([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rows, setRows] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState(false);

  const selectionBid = React.useContext(BidContext);
  const getToken = useToken();

  useEffect(() => {
    const fetchData = async () => {
      const token = await getToken();
      fetch(`${import.meta.env.VITE_APP_API_URL}/historical_cases_route`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.accessToken}`,
        },
        body: JSON.stringify(selectionBid),
      })
        .then((response) => response.json())
        .then((data) => {
          setHistoricalCases(data['Historical_Cases']);
          setOptions(getOptions(data['Historical_Cases']));
          setSelectedOptions(filterPreSelectedOptions(getOptions(data['Historical_Cases'])));
          setFiscalYear([
            'All',
            ...getDataByColumn(data['Historical_Cases'], 'Fiscal_Year'),
          ]);
          setEndCustomers([
            'All',
            ...getDataByColumn(data['Historical_Cases'], 'Endcustomer_Name'),
          ]);
        })
        .catch((error) => {
          console.log('Error fetching data from the API', error);
        })
        .finally(() => setLoading(false));
    };

    if (!Object.keys(selectionBid).length) return;
    fetchData();
  }, [selectionBid]);

  const handleChange = (value: Option[]) => {
    setSelectedOptions(value);
  };

  const filterPreSelectedOptions = (data: Option[]) => {
    return data.filter((item) => {
      return preSelectedColumns.includes(item.key);
    }).sort((a, b) => preSelectedColumns.indexOf(a.key) - preSelectedColumns.indexOf(b.key));
  };

  useEffect(() => {
    setHeaders(getHeaders());
    setRows(getRows());
  }, [selectedOptions, selectedEndCustomer, selectedFiscalYear]);

  const getDataByColumn = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: { [key: string]: any }[],
    column: string,
  ) => {
    const dataSet = new Set(data.map((item) => item[column]));
    return dataSet.size >= 1 ? Array.from(dataSet) : [];
  };

  const getOptions = (data: string[]) => {
    if (data.length > 0) {
      return Object.keys(data[0]).map((item) => {
        return { label: item, key: item };
      });
    }
    return [];
  };

  const getHeaders = () => {
    if (historicalCases.length > 0) {
      return selectedOptions
        .map((elem) => elem.key)
        .map((item) => {
          return {
            field: item,
            headerName: item,
            width: 150,
          };
        });
    }
    return [];
  };

  const getRows = () => {
    if (historicalCases.length > 0) {
      const filteredData = historicalCases.filter((item) => {
        return (
          (selectedEndCustomer === 'All' ||
            item['Endcustomer_Name'] === selectedEndCustomer) &&
          (selectedFiscalYear === 'All' ||
            item['Fiscal_Year'] === Number(selectedFiscalYear))
        );
      });
      const a = filteredData.map((item, index) => {
        return {
          id: index.toString(),
          ...(typeof item === 'object' ? item : {}),
        };
      });
      return a;
    }
    return [];
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <div className="similarCases">
      <h5>Similar Cases</h5>

      <div className="similarCases-legend">
        <span>
          Income start with income level of offer country, then the income level
          of end country
          <p>
            High income = 4, Upper middle income = 3, Lower middle income = 2,
            Low income = 1
          </p>
        </span>
      </div>
      {loading ? (
        <LoadingSpinner
          color="#8a00e5"
          backgroundColor="#ADA9C2"
          text="Loading..."
        />
      ) : (
        <div className="similarCases-results">
          <div className="similarCases-results-section__two">
            <div>
              <Select
                label="Select End Customer Name"
                id="endCustomer"
                onChange={({ target }) => setselectedEndCustomer(target.value)}
              >
                {endCustomers.map((item, index) => (
                  <SelectOption key={index} label={item} value={item} />
                ))}
              </Select>
            </div>
            <div>
              <Select
                label="Select Fiscal Year"
                id="fiscalYear"
                onChange={({ target }) => setselectedFiscalYear(target.value)}
              >
                {fiscalYear.map((item, index) => (
                  <SelectOption key={index} label={item} value={item} />
                ))}
              </Select>
            </div>
          </div>
          <Autocomplete
            id={''}
            options={options}
            multiple={true}
            selected={selectedOptions}
            onChange={(elem) => handleChange(elem)}
          />
          {historicalCases.length > 0 ? (
            <>
              <div className="similarCases-results-table-button">
                <ButtonIcon
                  onClick={handleOpenModal}
                  icon="IconFullscreen"
                  hoverType="none"
                />
              </div>
              <div className="similarCases-results-table">
                <DataGrid columns={headers} rows={rows} />
              </div>
            </>
          ) : (
            <h4 className="similarCases-results-notFound">
              No similar cases found
            </h4>
          )}
        </div>
      )}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Fullscreen Table</DialogTitle>
        <DialogContent>
          <div style={{ height: 600, width: '100%' }}>
            <DataGrid columns={headers} rows={rows} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SimilarCases;
